<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');
const highlightedIndex = ref(0);
const openUpward = ref(false);
const rootEl = ref(null);
const searchInputRef = ref(null);

const showSearch = computed(() => false);

const filteredOptions = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return props.options;
  return props.options
    .filter(o => o.label.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
    );
});

const selectedLabel = computed(() => {
  const found = props.options.find(o => o.value === props.modelValue);
  return found ? found.label : props.placeholder;
});

const isActive = computed(
  () => props.modelValue !== '' && props.modelValue !== null
);

watch(filteredOptions, () => {
  highlightedIndex.value = 0;
});

function checkDirection() {
  if (!rootEl.value) return;
  const rect = rootEl.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  openUpward.value = spaceBelow < 240;
}

function select(value) {
  if (value !== props.modelValue) {
    emit('update:modelValue', value);
  }
  open.value = false;
  search.value = '';
}

function toggle() {
  if (props.disabled) return;
  if (!open.value) {
    checkDirection();
  }
  open.value = !open.value;
  if (!open.value) search.value = '';
}

function onKeydown(e) {
  if (!open.value) return;
  if (e.key === 'Escape') {
    open.value = false;
    search.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      filteredOptions.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = filteredOptions.value[highlightedIndex.value];
    if (item) select(item.value);
  } else if (e.key === 'Backspace') {
    if (document.activeElement !== searchInputRef.value) {
      search.value = search.value.slice(0, -1);
    }
  } else if (
    e.key.length === 1 &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey &&
    !e.isComposing
  ) {
    if (document.activeElement !== searchInputRef.value) {
      search.value += e.key;
    }
  }
}

watch(
  open,
  val => {
    if (val) {
      window.addEventListener('keydown', onKeydown);
    } else {
      window.removeEventListener('keydown', onKeydown);
      search.value = '';
    }
  },
  { flush: 'sync' }
);

function onClickOutside(e) {
  if (rootEl.value && !rootEl.value.contains(e.target)) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  document.removeEventListener('mousedown', onClickOutside);
});
</script>

<template>
  <div ref="rootEl" class="relative" data-flow-select>
    <button
      type="button"
      :disabled="disabled"
      :aria-expanded="open"
      aria-haspopup="listbox"
      class="!h-8 w-full flex items-center justify-between gap-1 border rounded-lg transition-colors focus:outline-none px-3 text-sm"
      :class="[
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        isActive
          ? 'border-n-brand text-n-brand bg-n-alpha-2'
          : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container',
      ]"
      @click="toggle"
    >
      <span
        v-if="open && search && !showSearch"
        class="text-n-slate-10 truncate"
        >{{ search }}
      </span>
      <span v-else class="truncate">{{ selectedLabel }}</span>
      <span
        class="i-lucide-chevron-down size-3.5 flex-shrink-0 text-n-slate-10"
      />
    </button>
    <div
      v-if="open"
      role="listbox"
      class="absolute left-0 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-full w-max"
      :class="openUpward ? 'bottom-full mb-1' : 'top-full mt-1'"
      @click.stop
    >
      <!-- Campo de busca (apenas quando options.length > 5) -->
      <div v-if="showSearch" class="px-2 pt-1 pb-1 border-b border-n-weak">
        <div
          class="flex items-center gap-1.5 rounded-md border border-n-weak bg-n-surface-2 px-2 py-1"
        >
          <span class="i-lucide-search size-3 text-n-slate-10 flex-shrink-0" />
          <input
            ref="searchInputRef"
            v-model="search"
            type="text"
            :placeholder="t('FLOWS.CONFIG.SELECT.SEARCH_PLACEHOLDER')"
            autofocus
            class="flex-1 bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
          />
        </div>
      </div>

      <!-- Lista de opções com scroll -->
      <div class="max-h-60 overflow-y-auto">
        <button
          v-for="(opt, idx) in filteredOptions"
          :key="opt.value"
          type="button"
          role="option"
          :aria-selected="opt.value === modelValue"
          :title="opt.label"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none text-left"
          :class="
            idx === highlightedIndex
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : opt.value === modelValue
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
          "
          @click="select(opt.value)"
        >
          <span class="flex-1 truncate">{{ opt.label }}</span>
          <span
            v-if="opt.value === modelValue"
            class="i-lucide-check size-3 text-n-brand flex-shrink-0 ml-auto"
          />
        </button>

        <!-- Estado vazio: busca sem resultado -->
        <p
          v-if="search && !filteredOptions.length"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('FLOWS.CONFIG.SELECT.NOTHING_FOUND') }}
        </p>
        <!-- Estado vazio: lista genuinamente vazia -->
        <p
          v-else-if="!search && !filteredOptions.length"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('FLOWS.CONFIG.SELECT.EMPTY') }}
        </p>
      </div>
    </div>
  </div>
</template>
